import { AddCircle, Person } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useState } from 'react';
import * as uuid from 'uuid';
import { ScheduleDetailAddedBusinessComponentDto, ScheduleDetailDto } from '../../dtos';
import { useAddBusinessComponentToScheduleDetailMutation } from '../../store/api/schedule-api';
import { StandardDialogHeader } from '../CommonComponents';
import { FormInput } from '../CoreLib/library';

export interface IScheduleDetailBusinessComponentQuickAddProps {
    scheduleDetail: ScheduleDetailDto;
    companyName: string;
    disabled?: boolean;
    tooltip?: string;
    onAdd?: (recordId: string) => void;
}

export const ScheduleDetailBusinessComponentQuickAdd: FC<IScheduleDetailBusinessComponentQuickAddProps> = ({ scheduleDetail, tooltip = 'Add New', companyName, disabled, onAdd = () => {} }) => {
    const [addBusinessComponent, { isLoading }] = useAddBusinessComponentToScheduleDetailMutation();
    const { enqueueSnackbar } = useSnackbar();
    const [addBusinessComponentIsVisible, setAddBusinessComponentIsVisible] = useState(false);
    const [newBusinessComponentName, setNewBusinessComponentName] = useState('');

    const handleClose = useCallback(() => {
        setAddBusinessComponentIsVisible(false);
        setNewBusinessComponentName('');
    }, []);

    const handleSave = useCallback(() => {
        let recordToAdd: ScheduleDetailAddedBusinessComponentDto = {
            id: uuid.v4(),
            scheduleDetailId: scheduleDetail.id,
            name: newBusinessComponentName,
        };

        addBusinessComponent({
            scheduleId: scheduleDetail.scheduleId,
            scheduleDetailId: scheduleDetail.id,
            userAddedBusinessComponent: recordToAdd,
        })
            .unwrap()
            .then(() => {
                onAdd(recordToAdd.id);
                handleClose();
            })
            .catch(() => {
                enqueueSnackbar('An error occurred while attempting to add business component', { variant: 'error' });
            });
    }, [addBusinessComponent, enqueueSnackbar, handleClose, newBusinessComponentName, onAdd, scheduleDetail.id, scheduleDetail.scheduleId]);

    return (
        <>
            <Tooltip title={tooltip}>
                <Box>
                    <IconButton disabled={disabled} color='primary' size='large' onClick={() => setAddBusinessComponentIsVisible(true)}>
                        <AddCircle fontSize='inherit' />
                    </IconButton>
                </Box>
            </Tooltip>
            <Dialog fullWidth maxWidth='sm' open={addBusinessComponentIsVisible} onClose={handleClose}>
                <StandardDialogHeader icon={<Person />} text='Add Business Component' />
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>Business Component will be added to {companyName} only for this matrix.</Typography>
                    <FormInput value={newBusinessComponentName} onChange={(e) => setNewBusinessComponentName(e.target.value)} label='Name' required fullWidth />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant='outlined'>
                        Cancel
                    </Button>
                    <LoadingButton loading={isLoading} onClick={handleSave} variant='contained' color='primary'>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
